import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ProviderConfig, AgentInfo, ConversationContext } from '@/types/provider'
import type {
  RagSuggestion,
  SuggestionSource,
  SourcePage,
  TranscriptMessage,
  ConversationSummary,
  ConversationAnalysis,
  CoachSuggestion,
  CoachStepUpdate,
  CoachPlaybookChanged,
} from '@/types/suggestion'
// v2.5.25 — shared grouper (core/v2.2.41). Replaces the local copy that
// previously lived at lines 142-185 of this file. Behavior identical;
// eliminates the duplicated code path between widget + portal + bot widget.
import { groupSourcesByDocument as sharedGroupSourcesByDocument } from '@adaptcx/shared'

const MAX_SUGGESTIONS = 6
const MAX_VISIBLE_TRANSCRIPT = 200
const TRANSCRIPT_PAGE_SIZE = 50

export const useWidgetStore = defineStore('widget', () => {
  // Auth & config
  const embedToken = ref<string | null>(null)
  const providerConfig = ref<ProviderConfig | null>(null)
  const jwt = ref<string | null>(null)
  const agent = ref<AgentInfo | null>(null)

  // Conversation
  const conversation = ref<ConversationContext | null>(null)

  // Suggestions
  const suggestions = ref<RagSuggestion[]>([])

  // Transcript: two-array pattern (ported from omniassist source)
  // fullTranscript — master data, plain array, NOT reactive, unlimited size
  // transcript — visible window, reactive ref, sliced from fullTranscript, capped for DOM perf
  const fullTranscript: TranscriptMessage[] = []
  const _messageMap = new Map<string, number>()  // message id → index in fullTranscript
  const transcript = ref<TranscriptMessage[]>([])
  const transcriptVisibleCount = ref(TRANSCRIPT_PAGE_SIZE)

  const summary = ref<ConversationSummary | null>(null)
  const analysis = ref<ConversationAnalysis | null>(null)

  // AI Coach
  const coachSuggestion = ref<CoachSuggestion | null>(null)
  const hasNewCoachSuggestion = ref(false)

  // Connection
  const connectionStatus = ref<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected')
  const error = ref<string | null>(null)

  // Genesys context (from interaction widget URL params)
  const gcHostOrigin = ref('')
  const gcTargetEnv = ref('')

  // Getters
  const isConnected = computed(() => connectionStatus.value === 'connected')
  const hasConversation = computed(() => conversation.value !== null)
  const visibleSuggestions = computed(() => suggestions.value.slice(-MAX_SUGGESTIONS))
  const mediaType = computed(() => conversation.value?.media_type || 'voice')

  // Actions -- Auth & Config
  function setEmbedToken(token: string) {
    embedToken.value = token
  }

  function setProviderConfig(config: ProviderConfig) {
    providerConfig.value = config
  }

  function setJwt(token: string) {
    jwt.value = token
  }

  function setAgent(info: AgentInfo) {
    agent.value = info
  }

  function setConnectionStatus(status: 'disconnected' | 'connecting' | 'connected' | 'error') {
    connectionStatus.value = status
  }

  function setError(msg: string | null) {
    error.value = msg
  }

  // Actions -- Conversation
  function setConversation(ctx: ConversationContext) {
    // v1.2.1: idempotent on same-conversation re-emit. Pre-v1.2.1 we
    // wiped fullTranscript / suggestions / summary / coach unconditionally,
    // which caused a transient Socket.IO reconnect on a live call to
    // wipe everything the agent had on screen. Now we only clear when
    // the conversation_id actually changed (Salesforce room switch,
    // 8x8 transfer, new call after end). Same-conv re-emits update
    // the context fields (media_type, queue_name, etc.) but leave
    // the accumulated transcript/suggestion state alone.
    const sameConv =
      conversation.value &&
      ctx.conversation_id &&
      conversation.value.conversation_id === ctx.conversation_id

    conversation.value = ctx

    if (sameConv) return

    suggestions.value = []
    fullTranscript.length = 0
    _messageMap.clear()
    transcriptVisibleCount.value = TRANSCRIPT_PAGE_SIZE
    transcript.value = []
    summary.value = null
    analysis.value = null
    coachSuggestion.value = null
    hasNewCoachSuggestion.value = false
  }

  function clearConversation() {
    conversation.value = null
    suggestions.value = []
    fullTranscript.length = 0
    _messageMap.clear()
    transcriptVisibleCount.value = TRANSCRIPT_PAGE_SIZE
    transcript.value = []
    summary.value = null
    analysis.value = null
    coachSuggestion.value = null
    hasNewCoachSuggestion.value = false
  }

  // Actions -- Suggestions
  function addSuggestion(s: RagSuggestion) {
    suggestions.value.push(s)
  }

  // v2.5.25 — local `groupSourcesByDocument` removed. Now imported from
  // `@adaptcx/shared` (core/v2.2.41). Local wrapper preserves the SourcePage
  // shape consumers in this file already use (`relevance_percent` field
  // name) by mapping from the shared util's `score` field.
  function groupSourcesByDocument(rawSources: SuggestionSource[]): SuggestionSource[] {
    const grouped = sharedGroupSourcesByDocument(
      rawSources,
      (s) => s.relevance_percent,
    )
    return grouped.map((g) => ({
      ...g,
      pages: g.pages.map((pg) => ({
        page: pg.page,
        relevance_percent: pg.score,
        chunk_id: pg.chunk_id,
        uri: pg.uri ?? undefined,
      })) as SourcePage[],
    }))
  }

  function addSuggestionSources(data: {
    message_id: string
    query: string
    original_question: string
    clean_query?: string
    clean_text?: string
    sources: RagSuggestion['sources']
    // v2.5.28 — V5 classifier structured signals.
    intent?: RagSuggestion['intent']
    topic?: RagSuggestion['topic']
    entities_v5?: RagSuggestion['entities_v5']
    action_type?: RagSuggestion['action_type']
  }) {
    // v2.5.37 — real suggestion arriving supersedes any prior noise-turn
    // coaching cues. Clear so the bulb feeds from this suggestion's
    // own follow_ups (or hides if it has none).
    coachingCues.value = null
    const groupedSources = groupSourcesByDocument(data.sources)
    const existing = suggestions.value.find(s => s.id === data.message_id)
    if (!existing) {
      suggestions.value.push({
        id: data.message_id,
        query: data.query,
        original_question: data.original_question,
        clean_query: data.clean_query || '',
        clean_text: data.clean_text || '',
        answer: '',
        sources: groupedSources,
        question_id: '',
        answer_id: '',
        ttft_ms: 0,
        total_ms: 0,
        cached: false,
        streaming: true,
        timestamp: Date.now(),
        intent: data.intent ?? null,
        topic: data.topic ?? null,
        entities_v5: data.entities_v5 ?? [],
        action_type: data.action_type ?? null,
      })
    } else {
      existing.sources = groupedSources
      if (data.clean_query) existing.clean_query = data.clean_query
      if (data.clean_text) existing.clean_text = data.clean_text
      if (data.intent !== undefined) existing.intent = data.intent
      if (data.topic !== undefined) existing.topic = data.topic
      if (data.entities_v5 !== undefined) existing.entities_v5 = data.entities_v5
      if (data.action_type !== undefined) existing.action_type = data.action_type
    }
  }

  function updateSuggestionStreaming(data: {
    message_id: string
    answer: string
    index: number
    query?: string
    original_question?: string
    clean_query?: string
    clean_text?: string
    sources?: RagSuggestion['sources']
    ttft_ms?: number
    total_ms?: number
  }) {
    // v2.5.37 — see addSuggestionSources comment.
    coachingCues.value = null
    let s = suggestions.value.find(s => s.id === data.message_id)

    // First chunk (index -1 or 0) — create the card immediately so it appears
    // while streaming, matching the source's "typing effect" behaviour.
    if (!s) {
      s = {
        id: data.message_id,
        query: data.query || data.original_question || '',
        original_question: data.original_question || data.query || '',
        clean_query: data.clean_query || '',
        clean_text: data.clean_text || '',
        answer: '',
        sources: data.sources?.length ? groupSourcesByDocument(data.sources) : [],
        question_id: '',
        answer_id: '',
        ttft_ms: data.ttft_ms || 0,
        total_ms: data.total_ms || 0,
        cached: false,
        streaming: true,
        timestamp: Date.now(),
      }
      suggestions.value.push(s)
    }

    // index -1 is the initial sources-only event; >= 0 is an answer chunk
    if (data.index >= 0) {
      s.answer += data.answer
    }

    // Keep query/sources up-to-date if provided
    if (data.query && !s.query) {
      s.query = data.query
    }
    if (data.original_question && !s.original_question) {
      s.original_question = data.original_question
    }
    if (data.clean_query && !s.clean_query) s.clean_query = data.clean_query
    if (data.clean_text && !s.clean_text) s.clean_text = data.clean_text
    if (data.sources && data.sources.length && !s.sources?.length) {
      s.sources = groupSourcesByDocument(data.sources)
    }
    if (data.ttft_ms) s.ttft_ms = data.ttft_ms
    if (data.total_ms) s.total_ms = data.total_ms
  }

  function completeSuggestion(data: {
    message_id: string
    answer: string
    question_id: string
    answer_id: string
    query?: string
    original_question?: string
    clean_query?: string
    clean_text?: string
    // v2.5.28 — V5 classifier structured signals (also forwarded on complete-event).
    intent?: RagSuggestion['intent']
    topic?: RagSuggestion['topic']
    entities_v5?: RagSuggestion['entities_v5']
    action_type?: RagSuggestion['action_type']
    sources: RagSuggestion['sources']
    ttft_ms: number
    total_ms: number
    cached: boolean
    // v2.4.87 — refusal signal from rag-service. When true, the
    // streamed answer was the [NO_KB_MATCH] sentinel; replace it
    // with a clean empty state and drop the sources (they were
    // topical neighbors that don't actually contain the answer).
    no_answer?: boolean
    no_answer_reason?: string
  }) {
    // v2.5.37 — see addSuggestionSources comment.
    coachingCues.value = null
    const isNoAnswer = !!data.no_answer
    const groupedSources = data.sources?.length
      ? groupSourcesByDocument(data.sources)
      : []
    // v2.4.89 — when LLM refused, show the LLM's reason as the
    // answer (sentinel stripped). Keeps the useful information the
    // model already wrote — "the provided context does not contain
    // information regarding edit 262" — without the cryptic
    // [NO_KB_MATCH] prefix. Sources stay hidden via the no_answer flag.
    const resolvedAnswer = isNoAnswer
      ? (data.no_answer_reason || data.answer || '')
      : data.answer

    const s = suggestions.value.find(s => s.id === data.message_id)
    if (s) {
      s.answer = resolvedAnswer
      s.question_id = data.question_id
      s.answer_id = data.answer_id
      s.sources = isNoAnswer
        ? []
        : (groupedSources.length ? groupedSources : s.sources)
      s.ttft_ms = data.ttft_ms
      s.total_ms = data.total_ms
      s.cached = data.cached
      s.streaming = false
      s.no_answer = isNoAnswer
      s.no_answer_reason = data.no_answer_reason || ''
      // Backfill query if it wasn't set during streaming
      if (data.query && !s.query) s.query = data.query
      if (data.original_question && !s.original_question) s.original_question = data.original_question
      if (data.clean_query) s.clean_query = data.clean_query
      if (data.clean_text) s.clean_text = data.clean_text
      // v2.5.28 — V5 structured signals from rag-complete-event; merge
      // non-undefined values so the addSuggestionSources path's values
      // aren't accidentally cleared if complete-event omits them.
      if (data.intent !== undefined) s.intent = data.intent
      if (data.topic !== undefined) s.topic = data.topic
      if (data.entities_v5 !== undefined) s.entities_v5 = data.entities_v5
      if (data.action_type !== undefined) s.action_type = data.action_type
    } else {
      suggestions.value.push({
        id: data.message_id,
        query: data.query || data.original_question || '',
        original_question: data.original_question || data.query || '',
        clean_query: data.clean_query || '',
        clean_text: data.clean_text || '',
        answer: resolvedAnswer,
        sources: isNoAnswer ? [] : groupedSources,
        question_id: data.question_id,
        answer_id: data.answer_id,
        ttft_ms: data.ttft_ms,
        total_ms: data.total_ms,
        cached: data.cached,
        streaming: false,
        no_answer: isNoAnswer,
        no_answer_reason: data.no_answer_reason || '',
        timestamp: Date.now(),
        intent: data.intent ?? null,
        topic: data.topic ?? null,
        entities_v5: data.entities_v5 ?? [],
        action_type: data.action_type ?? null,
      })
    }
  }

  // v2.5.37 — Noise-turn coaching cues live OUTSIDE the suggestion
  // list. Replaces the v2.5.36 placeholder-card approach, which
  // produced an empty no-answer card in the suggestions list just to
  // anchor the follow-up bulb. Cleaner separation: suggestions are
  // reserved for meaningful turns with an answer; coaching cues are
  // their own parallel signal feeding the bulb + chat quick-replies.
  // The bulb's `followupQuestions` computed reads
  // `latestSuggestion.follow_ups || coachingCues.follow_ups`.
  // Cleared at the start of every suggestion-update handler so a
  // meaningful turn after a noise turn supersedes stale coaching.
  type CoachingCues = {
    message_id: string
    query: string
    original_question: string
    follow_ups: string[]
    quick_replies: string[]
  }
  const coachingCues = ref<CoachingCues | null>(null)

  function setFollowUps(data: {
    message_id: string
    follow_up_questions: string[]
    query?: string
    original_question?: string
  }) {
    const s = suggestions.value.find(s => s.id === data.message_id)
    if (s) {
      s.follow_ups = data.follow_up_questions
      return
    }
    // v2.5.37 — no matching suggestion = noise turn. Store as
    // standalone coaching cues. NO placeholder card created.
    coachingCues.value = {
      message_id: data.message_id,
      query: data.query || data.original_question || '',
      original_question: data.original_question || data.query || '',
      follow_ups: data.follow_up_questions || [],
      quick_replies: [],
    }
  }

  function setQuickReplies(data: {
    message_id: string;
    quick_replies: string[];
    // v2.5.22 — the backend's `rag-quickreplies-event` payload carries
    // BOTH quick_replies and follow_up_questions (see
    // assist-connector/handlers/rag_trigger.py:659-670 and :854-863).
    // The vestigial setFollowUps handler is wired to a `rag-followup-event`
    // the backend never emits — read both fields here so the voice
    // surface's follow-up section has data to render.
    follow_up_questions?: string[];
    // v2.5.36 — query / original_question included in the noise-emit
    // path (rag_trigger.py:662-664). v2.5.37 — still used, just now
    // to seed `coachingCues` instead of a placeholder suggestion.
    query?: string;
    original_question?: string;
  }) {
    const s = suggestions.value.find(s => s.id === data.message_id)
    if (s) {
      s.quick_replies = data.quick_replies
      if (data.follow_up_questions) {
        s.follow_ups = data.follow_up_questions
      }
      return
    }
    // v2.5.37 — noise turn. Store as standalone coaching cues. NO
    // placeholder card created.
    coachingCues.value = {
      message_id: data.message_id,
      query: data.query || data.original_question || '',
      original_question: data.original_question || data.query || '',
      follow_ups: data.follow_up_questions || [],
      quick_replies: data.quick_replies || [],
    }
  }

  // Actions -- Transcript (two-array pattern)
  // Mutate fullTranscript (plain array, no reactivity cost),
  // then slice into transcript ref (new array → Vue re-renders).

  function addTranscriptMessage(m: TranscriptMessage, _source: string = 'unknown') {
    const parentId = m.id
    if (!parentId) return

    if (_messageMap.has(parentId)) {
      // Update existing entry in the master array (direct mutation, no Vue overhead)
      const index = _messageMap.get(parentId)!
      const existing = fullTranscript[index]
      existing.text = m.text
      existing.timestamp = m.timestamp
      if (m.isRecognition !== undefined) existing.isRecognition = m.isRecognition
      if (m.is_final !== undefined) existing.is_final = m.is_final
      if (m.confidence !== undefined) existing.confidence = m.confidence
      if (m.translation !== undefined) existing.translation = m.translation
      if (m.role) existing.role = m.role
    } else {
      // New entry
      const index = fullTranscript.length
      fullTranscript.push(m)
      _messageMap.set(parentId, index)
    }

    // Slice into visible window — new array reference triggers Vue re-render
    _updateVisibleTranscript()
  }

  function _updateVisibleTranscript(): void {
    const total = fullTranscript.length
    if (total === 0) {
      transcript.value = []
      return
    }
    const count = Math.min(transcriptVisibleCount.value, total, MAX_VISIBLE_TRANSCRIPT)
    const start = Math.max(0, total - count)
    transcript.value = fullTranscript.slice(start)
  }

  function loadMoreTranscript(): void {
    if (transcriptVisibleCount.value >= MAX_VISIBLE_TRANSCRIPT) return
    if (transcriptVisibleCount.value >= fullTranscript.length) return
    transcriptVisibleCount.value = Math.min(
      transcriptVisibleCount.value + TRANSCRIPT_PAGE_SIZE,
      fullTranscript.length,
      MAX_VISIBLE_TRANSCRIPT,
    )
    _updateVisibleTranscript()
  }

  // Actions -- Coaching
  function setCoachSuggestion(data: CoachSuggestion) {
    coachSuggestion.value = data
    hasNewCoachSuggestion.value = true
  }

  function updateCoachStep(data: CoachStepUpdate) {
    if (!coachSuggestion.value || coachSuggestion.value.type !== 'deterministic') return
    const steps = coachSuggestion.value.steps
    if (steps) {
      const step = steps.find(s => s.index === data.completed_step_index)
      if (step) {
        step.completed = true
      }
    }
  }

  function onCoachPlaybookChanged(data: CoachPlaybookChanged) {
    // Clear current suggestion — new one will arrive via coach-suggestion
    coachSuggestion.value = null
    hasNewCoachSuggestion.value = true
  }

  function dismissCoachDot() {
    hasNewCoachSuggestion.value = false
  }

  // Actions -- Summary
  function setSummary(s: ConversationSummary) {
    summary.value = s
  }

  // Actions -- Analysis
  function setAnalysis(a: ConversationAnalysis) {
    analysis.value = a
  }

  return {
    // State
    embedToken,
    providerConfig,
    jwt,
    agent,
    conversation,
    suggestions,
    transcript,
    summary,
    analysis,
    coachSuggestion,
    hasNewCoachSuggestion,
    connectionStatus,
    error,
    gcHostOrigin,
    gcTargetEnv,
    // v2.5.37 — noise-turn coaching cues (standalone, not a suggestion).
    coachingCues,

    // Getters
    isConnected,
    hasConversation,
    visibleSuggestions,
    mediaType,

    // Actions
    setEmbedToken,
    setProviderConfig,
    setJwt,
    setAgent,
    setConnectionStatus,
    setError,
    setConversation,
    clearConversation,
    addSuggestion,
    addSuggestionSources,
    updateSuggestionStreaming,
    completeSuggestion,
    setFollowUps,
    setQuickReplies,
    addTranscriptMessage,
    loadMoreTranscript,
    setCoachSuggestion,
    updateCoachStep,
    onCoachPlaybookChanged,
    dismissCoachDot,
    setSummary,
    setAnalysis,
  }
})
